/**
 * Buy X Get Y (BXY) discount-code offers.
 *
 * Standalone module, independent from product-offers.js's own discount/GWP handling.
 * When the shopper types CHOCOLOVE or MAKEUPLOVE into the manual discount code field,
 * this intercepts the click (capture phase, before product-offers.js's own handler)
 * and instead of running the normal Shopify-discount-application flow, checks the
 * subtotal of cart items tagged for that offer against its configured threshold and
 * adds/removes ALL of the offer's configured free gift variants accordingly (every
 * product selected in the offer's product_list Theme Setting, not just the first).
 * Any other code is left untouched and falls through to the existing handler in
 * product-offers.js.
 *
 * Note: this only adds gift line items and tags them with the _bxy_free_gift property -
 * it cannot make them $0. That requires a separate Shopify Automatic Discount (Admin >
 * Discounts) targeting each gift variant at 100% off, since no client-side cart call can
 * set a line item's price.
 */
(function () {
  var STORAGE_KEY = 'bxyDiscountCode';
  var GIFT_PROPERTY = '_bxy_free_gift';

  function getOffers() {
    var el = document.getElementById('bxy-offer-settings');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  function getCartItemTagsMap() {
    var el = document.querySelector('[data-cart-item-tags-map]');
    var map = {};
    if (el) {
      try {
        map = JSON.parse(el.textContent) || {};
      } catch (e) {
        map = {};
      }
    }
    window.cartItemTagsMap = map;
    return map;
  }

  // Resolves a cart line item's product tags from whichever source is actually available.
  // window.cartItemTagsMap is refreshed on load/cart:updated, but if it's missing, stale, or
  // cleared by an AJAX drawer swap before we get to it, fall back to whatever /cart.js itself
  // returned, then finally to a data attribute rendered directly on the item's own markup.
  function resolveItemTags(item) {
    var tags = null;
    var map = window.cartItemTagsMap;

    if (map) {
      if (map[item.id] !== undefined) {
        tags = map[item.id];
      } else if (map[item.product_id] !== undefined) {
        tags = map[item.product_id];
      }
    }

    if (tags == null && Array.isArray(item.product_tags)) {
      tags = item.product_tags;
    } else if (tags == null && typeof item.product_tags === 'string') {
      tags = item.product_tags.split(',');
    }

    if (tags == null && Array.isArray(item.tags)) {
      tags = item.tags;
    } else if (tags == null && typeof item.tags === 'string') {
      tags = item.tags.split(',');
    }

    if (tags == null) {
      var itemEl = document.querySelector(
        '.quick-cart-item[data-key="' + item.key + '"], .quick-cart__item[data-key="' + item.key + '"]'
      );
      var dataTags = itemEl && itemEl.getAttribute('data-product-tags');
      if (dataTags) tags = dataTags.split(',');
    }

    if (!Array.isArray(tags)) return [];
    return tags.map(function (tag) {
      return String(tag).trim();
    });
  }

  function findOfferByCode(offers, code) {
    if (!code) return null;
    var upper = code.trim().toUpperCase();
    var keys = Object.keys(offers);
    for (var i = 0; i < keys.length; i++) {
      var offer = offers[keys[i]];
      if (offer && offer.code && offer.code.toUpperCase() === upper) {
        return offer;
      }
    }
    return null;
  }

  function getCart() {
    return fetch('/cart.js').then(function (response) {
      return response.json();
    });
  }

  // Every product selected in the offer's product_list setting, filtered down to only the
  // ones that actually resolved to a usable variant (see bxy-offer-settings.liquid).
  function getGiftEntries(offer) {
    return (offer.products || []).filter(function (entry) {
      return entry && entry.variantId;
    });
  }

  function isBxyGift(item) {
    if (!item || !item.properties) return false;
    var value = item.properties[GIFT_PROPERTY];
    return value === true || value === 'true';
  }

  // All current cart line items that are BXY gifts matching any of the given variant IDs.
  function findGiftLineItems(cartItems, variantIds) {
    var idSet = {};
    (variantIds || []).forEach(function (id) {
      if (id) idSet[id] = true;
    });
    return cartItems.filter(function (item) {
      return idSet[item.id] && isBxyGift(item);
    });
  }

  function taggedSubtotal(cartItems, offerTag) {
    var subtotal = 0;
    var targetTag = String(offerTag || '').toLowerCase();
    cartItems.forEach(function (item) {
      var tags = resolveItemTags(item);
      var isEligible = tags.some(function (tag) {
        return tag.toLowerCase() === targetTag;
      });
      if (isEligible) {
        subtotal += item.final_line_price;
      }
    });
    return subtotal;
  }

  // Adds all given gift entries in a single /cart/add.js call (items: [...] form) so they
  // land together rather than racing each other with separate sequential requests.
  function addGifts(entries) {
    var items = entries.map(function (entry) {
      var properties = {};
      properties[GIFT_PROPERTY] = true;
      return { id: entry.variantId, quantity: 1, properties: properties };
    });

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          // /cart/add.js resolves at the network level even on a 422 (e.g. sold-out or
          // invalid variant) - surface that as a real failure instead of a silent no-op.
          throw new Error(data && data.description ? data.description : 'Failed to add gifts to cart.');
        }
        return data;
      });
    });
  }

  // Removes all given cart line items (by key) in a single /cart/update.js call.
  function removeGiftLines(lineItems) {
    if (!lineItems.length) return Promise.resolve();
    var updates = {};
    lineItems.forEach(function (item) {
      updates[item.key] = 0;
    });
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });
  }

  // Notifies the rest of the theme a gift was added so the drawer UI reflects it. theme.js's
  // own cart:updated DOM event only fires for mutations made through its own cart.addItemById
  // wrapper, not our raw /cart/add.js fetch, so we dispatch it (and cart:refresh) ourselves,
  // then still call window.updateCart() to trigger the theme's actual AJAX drawer refresh.
  // A full page reload is only used as a last resort if that theme hook isn't present at all.
  //
  // custom-events.js documents cart:updated's contract as carrying detail.cart (the cart
  // object) - other listeners rely on that shape, so fetch a fresh cart before dispatching
  // rather than firing a bare event that violates it.
  function refreshCartUI() {
    return getCart()
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));

        if (typeof window.updateCart === 'function') {
          window.updateCart();
        } else {
          // Fallback if no custom theme event listener exists.
          window.location.reload();
        }
      });
  }

  function getErrorEl() {
    return document.getElementById('discount-code-error');
  }

  function setError(message) {
    var el = getErrorEl();
    if (el) el.textContent = message || '';
  }

  function getActiveCode() {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
      return '';
    }
  }
  // Actually applies the real Shopify discount code to the cart (this is what makes the
  // gift line item's price reflect as free — addGifts() alone never does this).
  function applyCartDiscount(code) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code })
    }).then(function (response) {
      return response.json();
    });
  }

  // Detaches any discount code currently applied to the cart.
  function removeCartDiscount() {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: '' })
    }).then(function (response) {
      return response.json();
    });
  }
  function setActiveCode(code) {
    try {
      if (code) {
        localStorage.setItem(STORAGE_KEY, code);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      // localStorage unavailable (private mode, etc.) - offer still works for this page view.
    }
  }

  var isProcessing = false;

  function evaluateOffer(code) {
    if (isProcessing) return Promise.resolve();
    isProcessing = true;

    var offers = getOffers();
    var offer = findOfferByCode(offers, code);

    if (!offer) {
      isProcessing = false;
      return Promise.resolve();
    }

    console.log('BXY Offer Config:', JSON.stringify(offer));

    getCartItemTagsMap();

    var giftEntries = getGiftEntries(offer);
    var variantIds = giftEntries.map(function (entry) {
      return entry.variantId;
    });

    return getCart()
      .then(function (cart) {
        var eligibleSubtotal = taggedSubtotal(cart.items, offer.tag);
        var thresholdCents = (offer.threshold || 0) * 100;
        var existingGiftLines = findGiftLineItems(cart.items, variantIds);
        var existingVariantIds = existingGiftLines.map(function (item) {
          return item.id;
        });

        console.log('Eligible Subtotal:', eligibleSubtotal);

        if (eligibleSubtotal >= thresholdCents) {
          if (!giftEntries.length) {
            if (!offer.products || !offer.products.length) {
              console.error('BXY Error: No gift product selected in Theme Settings for', offer.code, '- pick one under Theme Settings > Buy X Get Y.');
            } else {
              console.error('BXY Error: None of the gift products selected for', offer.code, 'have a resolvable variant (metafield unset and no available variant).');
            }
            setError('Free gift product is not configured correctly in Theme Settings.');
            return null;
          }

          setError('');

          var missingEntries = giftEntries.filter(function (entry) {
            return existingVariantIds.indexOf(entry.variantId) === -1;
          });

    showLoader();

    var addPromise = missingEntries.length
      ? addGifts(missingEntries)
      : Promise.resolve();

    return addPromise
      .then(function () {
        return applyCartDiscount(offer.code);
      })
      .then(function () {
        console.log('Discount applied, refreshing cart UI.');
        return refreshCartUI();
      })
      .finally(function () {
        hideLoader();
      });
            }
    showLoader();

    var removePromise = existingGiftLines.length
      ? removeGiftLines(existingGiftLines).then(function () {
          if (window.updateCart) window.updateCart();
        })
      : Promise.resolve();

    return removePromise
      .then(function () {
        return removeCartDiscount();
      })
      .then(function () {
        if (eligibleSubtotal === 0) {
          setError('No items in cart qualify for code ' + offer.code + '.');
        } else {
          var remaining = (thresholdCents - eligibleSubtotal) / 100;
          setError('Add ₹' + remaining.toFixed(2) + ' more of eligible items to unlock the free gift.');
        }
      })
      .finally(function () {
        hideLoader();
      });
      })
      .catch(function (error) {
        console.error('BXY offer evaluation failed:', error);
      })
      .finally(function () {
        isProcessing = false;
      });
  }

  function removeAllGifts() {
    return getCart().then(function (cart) {
      var offers = getOffers();
      var allVariantIds = [];
      Object.keys(offers).forEach(function (key) {
        getGiftEntries(offers[key]).forEach(function (entry) {
          allVariantIds.push(entry.variantId);
        });
      });
      var lineItems = findGiftLineItems(cart.items, allVariantIds);
    return removeGiftLines(lineItems)
      .then(function () {
        return removeCartDiscount();
      })
      .then(function () {
        if (window.updateCart) window.updateCart();
      });
    });
  }
  function showLoader() {
    var loaderParent = document.querySelector(".quick-cart__item");
    if (loaderParent) {
      loaderParent.classList.add("pending-quantity-update");
    }
  }

  function hideLoader() {
    var loaderParent = document.querySelector(".quick-cart__item");
    if (loaderParent) {
      loaderParent.classList.remove("pending-quantity-update");
    }
  }
  function handleClear() {
    setActiveCode('');
    setError('');
    removeAllGifts().catch(function (error) {
      console.error('BXY offer clear failed:', error);
    });
  }

  function handleApplyClick(e) {
    var applyBtn = e.target.closest('#apply-discount-btn, .apply-manual-code');
    if (!applyBtn) return;

    // Read the typed code and check for a BXY match BEFORE touching the event at all -
    // product-offers.js's own apply handler must run unimpeded for every other code.
    var input = document.getElementById('discount-code-input');
    var code = input ? input.value.trim() : '';
    var offer = findOfferByCode(getOffers(), code);

    if (!offer) {
      // Switching away from a previously-applied BXY code to something else - drop its gift.
      if (getActiveCode()) handleClear();
      return; // Not a BXY code - let the existing handler run normally.
    }

    console.log('BXY Code Detected:', code);

    // Only now, since the code is a BXY offer, stop product-offers.js's own apply handler
    // from also running for this click - BXY codes aren't real Shopify discount codes.
    e.preventDefault();
    e.stopImmediatePropagation();

    var previousCode = getActiveCode();
    setActiveCode(offer.code);

    if (previousCode && previousCode.toUpperCase() !== offer.code.toUpperCase()) {
      // Only one code can be active via this single input - switching from a different BXY
      // offer to this one must drop whatever gift(s) the old one added first, since
      // evaluateOffer only ever manages the gifts belonging to the offer it's given.
      console.log('Switching BXY offer from', previousCode, 'to', offer.code, '- clearing previous gift(s).');
      removeAllGifts()
        .catch(function (error) {
          console.error('BXY offer switch cleanup failed:', error);
        })
        .then(function () {
          evaluateOffer(offer.code);
        });
    } else {
      evaluateOffer(offer.code);
    }
  }

  function handleClearClick(e) {
    var clearBtn = e.target.closest('#clear-discount-btn');
    if (!clearBtn) return;
    handleClear();
  }

  function init() {
    getCartItemTagsMap();
    console.log('BXY Offers Loaded:', JSON.stringify(getOffers()));

    document.addEventListener(
      'click',
      function (e) {
        handleApplyClick(e);
        handleClearClick(e);
      },
      true
    );

    document.addEventListener('cart:updated', function () {
      getCartItemTagsMap();
      var code = getActiveCode();
      if (code) evaluateOffer(code);
    });

    var savedCode = getActiveCode();
    if (savedCode) {
      evaluateOffer(savedCode);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
